import { Button, Dialogo, Field } from '@qr/ui';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Preguntas y confirmaciones, con ventanas propias.
 *
 * Antes se usaban `confirm()` y `prompt()` del navegador. Funcionaban, pero
 * cada una venia con "app.apuntaqr.com dice" escrito encima por el navegador,
 * no se podian vestir ni traducir los botones, y congelaban la pestana entera
 * mientras estaban abiertas. En una herramienta que alguien usa a diario eso
 * se lee como un aviso del sistema, no como una pregunta del producto.
 *
 * La forma de usarlas se parece a las de antes a proposito, para que el sitio
 * donde se pregunta siga leyendose de arriba abajo:
 *
 *     const ok = await confirmar({ titulo: '...', mensaje: '...' });
 *     if (!ok) return;
 *
 * La diferencia es el `await`. Mas abajo se explica por que hace falta.
 */

interface PeticionDeConfirmar {
  readonly titulo: string;
  readonly mensaje: ReactNode;
  /** El texto del boton que sigue adelante. Por defecto, "Continuar". */
  readonly aceptar?: string;
  readonly cancelar?: string;
  /** Lo que no tiene vuelta atras: boton rojo y franja al lado del texto. */
  readonly grave?: boolean;
}

interface PeticionDeTexto {
  readonly titulo: string;
  readonly etiqueta: string;
  readonly aceptar?: string;
  readonly valorInicial?: string;
  readonly ayuda?: ReactNode;
  readonly maximo?: number;
}

interface Dialogos {
  readonly confirmar: (p: PeticionDeConfirmar) => Promise<boolean>;
  readonly pedirTexto: (p: PeticionDeTexto) => Promise<string | null>;
}

const Contexto = createContext<Dialogos | null>(null);

type Abierto =
  | { readonly tipo: 'confirmar'; readonly peticion: PeticionDeConfirmar }
  | { readonly tipo: 'texto'; readonly peticion: PeticionDeTexto }
  | null;

export function ProveedorDeDialogos({ children }: { readonly children: ReactNode }) {
  const [abierto, setAbierto] = useState<Abierto>(null);
  const [texto, setTexto] = useState('');

  /*
   * Quien respondera la promesa.
   *
   * Este es el truco que permite escribir `await confirmar(...)` dentro del
   * manejador, igual que se escribia `confirm(...)`. La alternativa habitual
   * —guardar en el estado que hay que abrir una ventana y continuar en otro
   * sitio cuando el usuario pulse— parte en dos cada accion: la mitad que
   * pregunta aqui y la mitad que actua alla. Con nueve sitios que preguntan,
   * eso son nueve trozos sueltos de los que nadie se acuerda.
   *
   * Va en una ref y no en el estado porque cambiarla no tiene que repintar
   * nada, y porque tiene que sobrevivir al repintado que si provoca abrir la
   * ventana.
   */
  const responder = useRef<((valor: never) => void) | null>(null);

  const cerrar = useCallback((valor: boolean | string | null) => {
    const f = responder.current as ((v: unknown) => void) | null;
    responder.current = null;
    setAbierto(null);
    setTexto('');
    f?.(valor);
  }, []);

  const confirmar = useCallback((peticion: PeticionDeConfirmar) => {
    return new Promise<boolean>((ok) => {
      responder.current = ok as never;
      setAbierto({ tipo: 'confirmar', peticion });
    });
  }, []);

  const pedirTexto = useCallback((peticion: PeticionDeTexto) => {
    return new Promise<string | null>((ok) => {
      responder.current = ok as never;
      setTexto(peticion.valorInicial ?? '');
      setAbierto({ tipo: 'texto', peticion });
    });
  }, []);

  const valor = useMemo<Dialogos>(
    () => ({ confirmar, pedirTexto }),
    [confirmar, pedirTexto],
  );

  return (
    <Contexto.Provider value={valor}>
      {children}

      {abierto?.tipo === 'confirmar' ? (
        <Dialogo
          abierto
          titulo={abierto.peticion.titulo}
          grave={abierto.peticion.grave ?? false}
          // Escape y el clic fuera valen como "no": cerrar sin responder
          // dejaria la promesa colgada para siempre y, con ella, el boton que
          // la espera.
          onCerrar={() => cerrar(false)}
          acciones={
            <>
              <Button variant="secondary" onClick={() => cerrar(false)}>
                {abierto.peticion.cancelar ?? 'Cancelar'}
              </Button>
              {/*
                Sin autoFocus, a proposito.

                Al abrirse con showModal() el foco cae en el primer boton, que
                es el de cancelar. Conviene que sea asi: quien llega con el
                teclado y pulsa Intro por inercia no borra nada. La accion que
                destruye hay que ir a buscarla.
              */}
              <Button
                variant={abierto.peticion.grave ? 'danger' : 'primary'}
                onClick={() => cerrar(true)}
              >
                {abierto.peticion.aceptar ?? 'Continuar'}
              </Button>
            </>
          }
        >
          {abierto.peticion.mensaje}
        </Dialogo>
      ) : null}

      {abierto?.tipo === 'texto' ? (
        <Dialogo
          abierto
          titulo={abierto.peticion.titulo}
          onCerrar={() => cerrar(null)}
          acciones={
            <>
              <Button variant="secondary" onClick={() => cerrar(null)}>
                Cancelar
              </Button>
              <Button
                // Enviar el formulario y no llamar al cierre directamente: asi
                // la tecla Intro dentro del campo hace lo mismo que el boton,
                // que es lo que espera cualquiera que acabe de teclear.
                type="submit"
                form="qr-dialogo-texto"
                disabled={texto.trim().length === 0}
              >
                {abierto.peticion.aceptar ?? 'Guardar'}
              </Button>
            </>
          }
        >
          <form
            id="qr-dialogo-texto"
            onSubmit={(e) => {
              e.preventDefault();
              const limpio = texto.trim();
              if (limpio.length > 0) cerrar(limpio);
            }}
          >
            <Field
              label={abierto.peticion.etiqueta}
              hint={abierto.peticion.ayuda}
              value={texto}
              maxLength={abierto.peticion.maximo}
              onChange={(e) => setTexto(e.target.value)}
              autoFocus
            />
          </form>
        </Dialogo>
      ) : null}
    </Contexto.Provider>
  );
}

function useDialogos(): Dialogos {
  const c = useContext(Contexto);
  if (!c) throw new Error('useDialogos debe usarse dentro de <ProveedorDeDialogos>.');
  return c;
}

export function useConfirmar(): Dialogos['confirmar'] {
  return useDialogos().confirmar;
}

export function usePedirTexto(): Dialogos['pedirTexto'] {
  return useDialogos().pedirTexto;
}
